import React, { useState, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { MyContext, ListContext } from "./MyContext";
import COLORS from "./styleConstants";
import "./elementsStyle.css";
import { Square, CheckSquare } from "./icons";
import CheckoutModal from "./checkoutModal";

import Auth from "./Auth";

export const CheckoutForm = ({
  isInvoicePayment = false,
  paymentId = "",
  currentPage = "purchases",
}) => {
  const { listState, dispatch2 } = useContext(ListContext);
  const { mystate, dispatch } = useContext(MyContext);
  const [saveCard, setSaveCard] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isMobile] = useState(window.innerWidth < 500);

  const elements = useElements();
  const stripe = useStripe();
  const location = useLocation();
  const intentRef = useRef(listState.paymentIntent);
  const submittingRef = useRef(false);

  const ensurePaymentIntent = async () => {
    if (intentRef.current?.id) return intentRef.current;
    const params = location.state?.pendingPaymentParams;
    if (!params) throw new Error("Please restart checkout.");
    const result = await Auth.api(
      "payments/createPaymentIntentAndCustomer/",
      "post",
      params,
      { skipGlobalErrorHandler: true },
    );
    const intent = result.data?.payment_intent;
    if (!intent?.id || !intent.client_secret) {
      throw new Error("Unable to start payment.");
    }
    intentRef.current = intent;
    dispatch2(["SET_PAYMENT_INTENT", intent]);
    return intent;
  };

  const options = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": { color: "#aab7c4" },
      },
      invalid: { color: "#fa755a", iconColor: "#fa755a" },
    },
  };

  const checkDivStyle = {
    margin: 10,
    padding: 20,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
  };

  const stripeInputIsReady = (el) => {
    el.focus();
    dispatch({ type: "FETCH_SUCCESS" });
  };

  const handleElementChange = (event) => {
    setIsEmpty(event.empty);
  };

  return (
    <CheckoutModal
      isInvoicePayment={isInvoicePayment}
      paymentId={paymentId}
      currentPage={currentPage}
    >
      {({ setProcessing, setError, onConfirmed, submitRef }) => {
        const handleSubmit = async (ev) => {
          ev.preventDefault();
          if (submittingRef.current || !stripe || !elements) return;

          if (isEmpty) {
            alert("PLEASE FILL IN  YOUR CARD DETAILS");
            return;
          }

          submittingRef.current = true;
          dispatch({ type: "FETCH_INIT" });
          setProcessing(true);
          setError(null);

          try {
            const intent = await ensurePaymentIntent();
            const payload = await stripe.confirmCardPayment(
              intent.client_secret,
              {
                payment_method: { card: elements.getElement(CardElement) },
                setup_future_usage: saveCard ? "on_session" : null,
              },
            );

            if (saveCard && payload.paymentIntent?.status === "succeeded") {
              try {
                await Auth.api(
                  "payments/updatePaymentMethod",
                  "post",
                  {
                    person_id: listState.accountHolder.id,
                    payment_method: payload.paymentIntent.payment_method,
                  },
                  { skipGlobalErrorHandler: true },
                );
              } catch (e) {
                console.log("e in paymentForm update payment method", e);
              }
            }

            if (payload.error) {
              setError(`Payment failed: ${payload.error.message}`);
              setProcessing(false);
              dispatch({ type: "FETCH_ERROR" });
              return;
            }

            if (
              payload.paymentIntent &&
              payload.paymentIntent.status === "requires_action"
            ) {
              alert(
                "Additional authentication is required. Please follow the instructions.",
              );
              setProcessing(false);
              return;
            }

            if (payload.paymentIntent?.status !== "succeeded") {
              throw new Error(
                "Payment was not completed. Please check the payment status before trying again.",
              );
            }
            await onConfirmed(payload.paymentIntent.id);
          } catch (e) {
            setError(`Payment failed: ${e.message}`);
            setProcessing(false);
            dispatch({ type: "FETCH_ERROR" });
          } finally {
            submittingRef.current = false;
            setProcessing(false);
            dispatch({ type: "FETCH_SUCCESS" });
          }
        };

        // Register this form's handler so the wrapper's pay button triggers it.
        submitRef.current = handleSubmit;

        return (
          <div
            className="sr-combo-inputs"
            style={{ border: "solid 1 grey", width: "100%" }}
          >
            <div style={checkDivStyle}>
              <div
                onClick={() => setSaveCard(!saveCard)}
                style={{ marginRight: 10, fontSize: 20 }}
              >
                {saveCard ? (
                  <CheckSquare fontSize={30} />
                ) : (
                  <Square fontSize={30} />
                )}
              </div>
              <div style={{ fontSize: 14, color: COLORS.textDark }}>
                {`Would you like to save this payment method for future use?`}
              </div>
            </div>
            <div className="sr-combo-inputs-row">
              <CardElement
                onReady={(el) => stripeInputIsReady(el)}
                className="payment sr-input sr-card-element"
                options={options}
                onChange={(event) => handleElementChange(event)}
              />
            </div>
          </div>
        );
      }}
    </CheckoutModal>
  );
};

export default CheckoutForm;
